"use client";

import { useState, useCallback, useEffect } from "react";
import { useAuth } from "@/hooks/auth/use-auth";
import type {
  AnalyticsDashboardData,
  AnalyticsPeriod,
} from "@/components/agents/email/analytics/types";

interface UseAgentAnalyticsOptions {
  agentId: string | null;
  period?: AnalyticsPeriod;
}

interface UseAgentAnalyticsReturn {
  data: AnalyticsDashboardData | null;
  isLoading: boolean;
  error: string | null;
  period: AnalyticsPeriod;
  customRange: { from: string; to: string } | null;
  tz: string;
  setPeriodAndRange: (
    period: AnalyticsPeriod,
    customRange?: { from: string; to: string },
  ) => void;
  refresh: () => void;
}

/**
 * Hook for fetching agent analytics data from the API.
 * Sends the user's timezone so period boundaries are timezone-aware.
 */
export function useAgentAnalytics({
  agentId,
  period: initialPeriod = "this_month",
}: UseAgentAnalyticsOptions): UseAgentAnalyticsReturn {
  const { user, fetchWithAuth, isAuthenticated } = useAuth();
  const [data, setData] = useState<AnalyticsDashboardData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState<AnalyticsPeriod>(initialPeriod);
  const [customRange, setCustomRange] = useState<{
    from: string;
    to: string;
  } | null>(null);

  const setPeriodAndRange = useCallback(
    (
      newPeriod: AnalyticsPeriod,
      newCustomRange?: { from: string; to: string },
    ) => {
      setPeriod(newPeriod);
      setCustomRange(
        newPeriod === "custom" && newCustomRange ? newCustomRange : null,
      );
    },
    [],
  );

  const tz = user?.timezone ?? "UTC";

  const loadData = useCallback(async () => {
    if (!agentId) {
      setData(null);
      setIsLoading(false);
      return;
    }

    if (!isAuthenticated) {
      setData(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({ period, tz });
      if (period === "custom" && customRange) {
        params.set("from", customRange.from);
        params.set("to", customRange.to);
      }

      const response = await fetchWithAuth(
        `/api/agents/email/${agentId}/analytics?${params.toString()}`,
      );

      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        throw new Error(
          (errBody as { error?: string }).error ?? `HTTP ${response.status}`,
        );
      }

      const result: AnalyticsDashboardData = await response.json();
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load analytics");
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }, [agentId, period, customRange, tz, fetchWithAuth, isAuthenticated]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  return {
    data,
    isLoading,
    error,
    period,
    customRange,
    tz,
    setPeriodAndRange,
    refresh: loadData,
  };
}
